import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { preview } from '@/components/form/preview'

import { ErrorOutline } from '@mui/icons-material'
import { Stack } from '@mui/material'
import { Chip } from '@workwave-tidal/core/components'

import { selectCompanies } from '@/features/domain/company'

import { useTexts } from './hooks/useTexts'

interface Props {
  companyIds: string[]
  isCompanyFiltered: boolean
}

export function Companies(props: Props) {
  const { companyIds, isCompanyFiltered } = props
  const companies = useCompanies(companyIds)

  const texts = useTexts()

  if (!isCompanyFiltered) return null

  return (
    <preview.Field
      label={texts.labels.companies(companies.length)}
      variant="alternative"
      data-trackid="companies"
      testId="companies"
    >
      <Stack gap={0.5} direction="row" flexWrap="wrap" alignItems="center">
        {companies.length === 0 ? (
          <>
            <ErrorOutline color="error" fontSize="small" />
            {texts.errors.companyMissing}
          </>
        ) : (
          companies.map(company => <Chip size="small" label={company.name} key={company.id} />)
        )}
      </Stack>
    </preview.Field>
  )
}

function useCompanies(companyIds: string[]): uui.domain.client.rm.Company[] {
  const companies = useSelector(selectCompanies)

  return useMemo(
    () => companyIds.map(id => companies[id]).sort(sortCompanies),
    [companies, companyIds],
  )
}

function sortCompanies(a: uui.domain.client.rm.Company, b: uui.domain.client.rm.Company) {
  return a.name.localeCompare(b.name)
}
